import {
  ETIQUETA_ENTIDAD_REVISION,
  ETIQUETA_ENTIDAD_REVISION_EN,
  ETIQUETA_SEVERIDAD,
  ETIQUETA_SEVERIDAD_EN,
  SEVERIDADES,
  type DetalleDeRevision,
  type Revision,
  type Severidad,
  type TableroDeCalidad,
} from '@cnc/contracts';
import {useEffect, useRef, useState} from 'react';
import {Link, useSearchParams} from 'react-router';
import {
  useIncidenciasDeCarga,
  useOpcionesDeColumnaDeRevision,
  useRevision,
  useTableroDeCalidad,
  type VistaDeRevision,
} from '../api/calidad';
import {FiltrosPorColumna} from '../componentes/filtros-por-columna';
import type {ColumnaConFiltro, UseOpcionesDeColumna} from '../utilidades/columnas-con-filtro';
import {AgenteDeCalidad} from '../componentes/agente-de-calidad';
import {CambioMasivoDeRevision, type CasosMarcados} from '../componentes/cambio-masivo-de-revision';
import {AYUDA_CALIDAD as A, AYUDA_CARGAS as C} from '../componentes/ayuda-general';
import {OpcionesDeBigBags} from '../componentes/celda-big-bag';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {usePermisos} from '../componentes/sesion-contexto';
import {et, tr, type Texto} from '../i18n/idioma';
import {fecha, fechaHora, numero, textoOGuion} from '../utilidades/formato';
import {casoDeEnlace, direccionParaCorregir} from '../utilidades/vuelta';

const TONO: Record<Severidad, string> = {
  error: 'border-alerta/40 bg-red-50 text-alerta',
  warning: 'border-dorado/60 bg-dorado-claro text-tinta',
  info: 'border-linea bg-papel text-apagado',
};

// Al cambiar de revision se quitan el orden y los filtros (cada una tiene sus columnas).
const SIN_VISTA = {orden: null, direccion: null, porColumna: null};

// Calidad de datos: revisiones sobre la base con sus casos, las reglas de
// clasificacion tal como estan en el codigo y las cargas con sus incidencias.
export function PaginaCalidad() {
  const [parametros, setParametros] = useSearchParams();
  const revisionActiva = parametros.get('revision');
  const cargaActiva = parametros.get('carga') ? Number(parametros.get('carga')) : null;
  const pagina = Math.max(1, Number(parametros.get('pagina') ?? 1) || 1);
  // Orden y filtros por columna de los casos (23/10/2026), en la direccion.
  const vista: VistaDeRevision = {
    orden: parametros.get('orden') ?? undefined,
    direccion: parametros.get('direccion') === 'desc' ? 'desc' : 'asc',
    porColumna: parametros.get('porColumna') ?? undefined,
  };
  const tablero = useTableroDeCalidad();
  const revision = useRevision(revisionActiva, pagina, vista);
  const incidencias = useIncidenciasDeCarga(cargaActiva);
  const severidades = et(ETIQUETA_SEVERIDAD, ETIQUETA_SEVERIDAD_EN);
  // Al abrir una alerta o una revision (tambien desde "Pendientes hoy"), la
  // pantalla baja hasta sus casos para revisarlos y corregirlos (6/10/2026).
  const detalleRef = useRef<HTMLDivElement>(null);
  const detalleListo = Boolean(revision.data);
  useEffect(() => {
    if (revisionActiva && detalleListo) detalleRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
  }, [revisionActiva, detalleListo]);
  // Direccion de esta revision, para volver despues de corregir un caso.
  const vuelta = `/calidad?${new URLSearchParams(
    Object.entries({
      revision: revisionActiva,
      pagina: pagina > 1 ? String(pagina) : null,
      orden: vista.orden ?? null,
      direccion: vista.orden && vista.direccion === 'desc' ? 'desc' : null,
      porColumna: vista.porColumna ?? null,
    }).filter((x): x is [string, string] => x[1] !== null),
  ).toString()}`;

  const ir = (cambios: Record<string, string | null>) => {
    const nuevos = new URLSearchParams(parametros);
    for (const [clave, valor] of Object.entries(cambios)) {
      if (valor === null) nuevos.delete(clave);
      else nuevos.set(clave, valor);
    }
    setParametros(nuevos, {replace: true});
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Calidad de datos', 'Data quality')}</h1>
          <p className="text-sm text-apagado">
            {tr(
              'Revisiones sobre la base, las reglas de clasificación tal como están en el código y el historial de cargas con sus incidencias. Las diferencias de exportaciones con ETA de 2024 o antes están aceptadas y no se revisan.',
              'Checks on the database, the classification rules exactly as they are in the code and the upload history with its issues. Differences in exports with an ETA in 2024 or earlier are accepted and not checked.',
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void tablero.refetch()}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
        >
          ↻ {tr('Actualizar', 'Refresh')}
        </button>
      </div>

      <AgenteDeCalidad onAbrir={clave => ir({revision: clave, pagina: null, carga: null, ...SIN_VISTA})} />

      {tablero.isPending && <p className="text-apagado">{tr('Revisando la base…', 'Checking the database…')}</p>}
      {tablero.isError && <p className="text-sm text-alerta">{tablero.error.message}</p>}
      {tablero.data && (
        <>
          <Totales tablero={tablero.data} />
          <div className="grid gap-3 lg:grid-cols-3">
            {SEVERIDADES.map(severidad => (
              <div key={severidad} className="space-y-2">
                <h2 className="text-xs font-semibold tracking-wide text-apagado uppercase">{severidades[severidad]}</h2>
                {tablero.data.revisiones
                  .filter(r => r.severidad === severidad)
                  .map(r => (
                    <TarjetaDeRevision
                      key={r.clave}
                      revision={r}
                      activa={revisionActiva === r.clave}
                      onAbrir={() =>
                        ir({
                          revision: revisionActiva === r.clave ? null : r.clave,
                          pagina: null,
                          carga: null,
                          ...SIN_VISTA,
                        })
                      }
                    />
                  ))}
              </div>
            ))}
          </div>

          {revisionActiva && (
            <div ref={detalleRef} className="scroll-mt-4">
              {/* Listas (minas, bodegas...) para el cambio masivo de Big Bags. */}
              <OpcionesDeBigBags />
              <DetalleRevision
                key={revisionActiva}
                detalle={revision.data}
                cargando={revision.isFetching}
                error={revision.isError ? revision.error.message : null}
                vuelta={vuelta}
                vista={vista}
                onPagina={p => ir({pagina: String(p)})}
                onVista={v =>
                  ir({
                    pagina: null,
                    orden: v.orden ?? null,
                    direccion: v.orden && v.direccion === 'desc' ? 'desc' : null,
                    porColumna: v.porColumna ?? null,
                  })
                }
                onCerrar={() => ir({revision: null, pagina: null, ...SIN_VISTA})}
              />
            </div>
          )}

          <Reglas tablero={tablero.data} />

          <Cargas
            tablero={tablero.data}
            cargaActiva={cargaActiva}
            onAbrir={id => ir({carga: cargaActiva === id ? null : String(id), revision: null, pagina: null})}
          />
          {cargaActiva !== null && incidencias.data && (
            <div className="rounded-lg border border-linea bg-white p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="font-medium">
                  {tr('Incidencias de la carga', 'Issues of the upload')} {textoOGuion(incidencias.data.carga.archivo)}{' '}
                  <span className="text-sm font-normal text-apagado">
                    ({numero(incidencias.data.total)}
                    {incidencias.data.total > incidencias.data.incidencias.length
                      ? tr(
                          `, se muestran ${numero(incidencias.data.incidencias.length)}`,
                          `, showing ${numero(incidencias.data.incidencias.length)}`,
                        )
                      : ''}
                    )
                  </span>
                </h2>
                <button
                  type="button"
                  onClick={() => ir({carga: null})}
                  className="text-sm text-apagado hover:text-tinta"
                >
                  ✕ {tr('Cerrar', 'Close')}
                </button>
              </div>
              {incidencias.data.incidencias.length === 0 ? (
                <p className="mt-2 text-sm text-apagado">
                  {tr('Esta carga no dejó incidencias.', 'This upload left no issues.')}
                </p>
              ) : (
                <div className="mt-2 max-h-[28rem] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                        <th className="py-1 pr-3 font-semibold">
                          {tr('Severidad', 'Severity')}
                          <NotaDeAyuda texto={A.severidad} />
                        </th>
                        <th className="py-1 pr-3 font-semibold">
                          {tr('Categoría', 'Category')}
                          <NotaDeAyuda texto={A.categoria} />
                        </th>
                        <th className="py-1 pr-3 font-semibold">
                          {tr('Registro', 'Record')}
                          <NotaDeAyuda texto={A.registro} />
                        </th>
                        <th className="py-1 pr-3 font-semibold">
                          {tr('Campo', 'Field')}
                          <NotaDeAyuda texto={A.campo} />
                        </th>
                        <th className="py-1 pr-3 font-semibold">
                          {tr('Archivo decía', 'File said')}
                          <NotaDeAyuda texto={A.archivoDecia} />
                        </th>
                        <th className="py-1 pr-3 font-semibold">
                          {tr('Quedó', 'Result')}
                          <NotaDeAyuda texto={A.quedo} />
                        </th>
                        <th className="py-1 pr-3 font-semibold">
                          {tr('Mensaje', 'Message')}
                          <NotaDeAyuda texto={A.mensaje} />
                        </th>
                        <th className="py-1 font-semibold">
                          {tr('Decisión', 'Decision')}
                          <NotaDeAyuda texto={A.decision} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {incidencias.data.incidencias.map(i => (
                        <tr key={i.id} className="border-t border-linea/70 align-top">
                          <td className="py-1 pr-3">
                            <span className={`rounded-full border px-2 py-0.5 text-xs ${TONO[i.severidad]}`}>
                              {severidades[i.severidad]}
                            </span>
                          </td>
                          <td className="py-1 pr-3">{i.categoria}</td>
                          <td className="py-1 pr-3">
                            <EnlaceDeRegistro entidad={i.entidad} clave={i.clave} />
                          </td>
                          <td className="py-1 pr-3">{textoOGuion(i.campo)}</td>
                          <td className="py-1 pr-3">{textoOGuion(i.valorArchivo)}</td>
                          <td className="py-1 pr-3">{textoOGuion(i.valorAplicado)}</td>
                          <td className="py-1 pr-3">{i.mensaje}</td>
                          <td className="py-1">{textoOGuion(i.decision)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Totales({tablero}: {tablero: TableroDeCalidad}) {
  const t = tablero.totales;
  const datos: Array<[string, string, string, Texto]> = [
    [
      tr('Errores', 'Errors'),
      numero(t.errores),
      tr('casos en revisiones de error', 'cases in error checks'),
      A.errores,
    ],
    [
      tr('Advertencias', 'Warnings'),
      numero(t.advertencias),
      tr('casos por revisar', 'cases to review'),
      A.advertencias,
    ],
    [
      tr('Información', 'Information'),
      numero(t.informativas),
      tr('casos informativos', 'informational cases'),
      A.informativas,
    ],
    [tr('Big Bags revisados', 'Big Bags checked'), numero(t.bigBags), tr('en la base', 'in the database'), A.bigBags],
    [
      tr('Exportaciones revisadas', 'Exports checked'),
      numero(t.exportaciones),
      tr('en la base', 'in the database'),
      A.exportaciones,
    ],
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
      {datos.map(([nombre, valor, detalle, ayuda]) => (
        <div key={nombre} className="rounded-lg border border-linea bg-white px-3 py-2">
          <div className="text-xs text-apagado">
            {nombre}
            <NotaDeAyuda titulo={nombre} texto={ayuda} />
          </div>
          <div className="text-base font-semibold tabular-nums text-verde-oscuro">{valor}</div>
          <div className="text-[11px] text-apagado">{detalle}</div>
        </div>
      ))}
    </div>
  );
}

function TarjetaDeRevision({revision, activa, onAbrir}: {revision: Revision; activa: boolean; onAbrir: () => void}) {
  return (
    <button
      type="button"
      onClick={onAbrir}
      className={`w-full rounded-lg border bg-white p-3 text-left hover:bg-papel ${
        activa ? 'border-verde ring-1 ring-verde' : 'border-linea'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium">{revision.titulo}</div>
        <span
          className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums ${TONO[revision.severidad]}`}
        >
          {numero(revision.cantidad)}
        </span>
      </div>
      <div className="mt-1 text-xs text-apagado">
        {et(ETIQUETA_ENTIDAD_REVISION, ETIQUETA_ENTIDAD_REVISION_EN)[revision.entidad]}
      </div>
      <p className="mt-1 line-clamp-3 text-xs text-apagado">{revision.descripcion}</p>
    </button>
  );
}

function DetalleRevision({
  detalle,
  cargando,
  error,
  vuelta,
  vista,
  onPagina,
  onVista,
  onCerrar,
}: {
  detalle: DetalleDeRevision | undefined;
  cargando: boolean;
  error: string | null;
  vuelta: string;
  vista: VistaDeRevision;
  onPagina: (pagina: number) => void;
  onVista: (vista: VistaDeRevision) => void;
  onCerrar: () => void;
}) {
  const permisos = usePermisos();
  // Casos marcados para corregir o aceptar varios a la vez: clave del caso → enlace.
  const [seleccion, setSeleccion] = useState<CasosMarcados>(new Map());
  const clave = detalle?.clave ?? '';
  const useOpciones: UseOpcionesDeColumna = (columna, contiene, habilitado) =>
    useOpcionesDeColumnaDeRevision(clave, vista.porColumna, columna, contiene, habilitado);
  if (error) return <p className="text-sm text-alerta">{error}</p>;
  if (!detalle) return <p className="text-sm text-apagado">{tr('Consultando…', 'Loading…')}</p>;
  // Clic en el titulo de una columna: ordena por ella; otro clic, al reves.
  const ordenarPor = (id: string) =>
    onVista({
      ...vista,
      orden: id,
      direccion: vista.orden === id && vista.direccion !== 'desc' ? 'desc' : 'asc',
    });
  const columnasConFiltro: ColumnaConFiltro[] = detalle.columnas.map(c => ({
    id: c.id,
    titulo: c.titulo,
    tipo: c.tipo ?? (c.numerica ? 'numero' : 'texto'),
  }));
  const primera = detalle.columnas[0]?.id;
  // "✎ Corregir": abre el Big Bag o la exportacion ya en edicion y vuelve aqui.
  const corregir = (enlace: string | null) =>
    permisos.editar && enlace ? direccionParaCorregir(enlace, vuelta) : null;
  const conCorregir = detalle.filas.some(f => corregir(f.enlace));
  // Corregir en lote: solo si todos los casos son del mismo tipo (Big Bags o
  // exportaciones). Aceptar: cualquier revision, con permiso de edicion.
  const tipos = new Set(detalle.filas.flatMap(f => casoDeEnlace(f.enlace)?.tipo ?? []));
  const tipo = tipos.size === 1 ? [...tipos][0]! : null;
  const marcable = permisos.editar;
  // Nombre del caso para leerlo (los grupos de incidencias tienen clave interna).
  const nombreDelCaso = (f: DetalleDeRevision['filas'][number]) =>
    f.clave.includes('|') && primera ? String(f.valores[primera] ?? f.clave) : f.clave;
  const deLaPagina = detalle.filas;
  const paginaMarcada = deLaPagina.length > 0 && deLaPagina.every(f => seleccion.has(f.clave));
  const marcar = (filas: Array<{clave: string; enlace: string | null}>, si: boolean) =>
    setSeleccion(actual => {
      const nueva = new Map(actual);
      for (const f of filas) {
        if (si) nueva.set(f.clave, f.enlace);
        else nueva.delete(f.clave);
      }
      return nueva;
    });
  return (
    <div className={`rounded-lg border border-verde/50 bg-white p-4 ${cargando ? 'opacity-70' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-medium text-verde-oscuro">
            {detalle.titulo} <span className="text-sm font-normal text-apagado">({numero(detalle.cantidad)})</span>
          </h2>
          <p className="text-xs text-apagado">{detalle.descripcion}</p>
          {detalle.aceptados > 0 && (
            <p className="mt-1 text-xs text-apagado">
              {detalle.accionDeAceptar === 'decidir'
                ? tr(
                    `${numero(detalle.aceptados)} incidencias ya están decididas.`,
                    `${numero(detalle.aceptados)} issues are already decided.`,
                  )
                : tr(
                    `${numero(detalle.aceptados)} casos aceptados no se muestran.`,
                    `${numero(detalle.aceptados)} accepted cases are not shown.`,
                  )}
            </p>
          )}
        </div>
        <button type="button" onClick={onCerrar} className="text-sm text-apagado hover:text-tinta">
          ✕ {tr('Cerrar', 'Close')}
        </button>
      </div>
      <FiltrosPorColumna
        columnas={columnasConFiltro}
        valor={vista.porColumna}
        onCambiar={porColumna => onVista({...vista, porColumna})}
        useOpciones={useOpciones}
        ayuda={tr(
          'Uno por cada columna de la lista. Clic en el título de una columna para ordenar.',
          'One per list column. Click a column title to sort.',
        )}
      />
      {marcable && detalle.filas.length > 0 && (
        <CambioMasivoDeRevision
          detalle={detalle}
          tipo={tipo}
          seleccion={seleccion}
          onSeleccion={setSeleccion}
          vista={vista}
        />
      )}
      {detalle.filas.length === 0 ? (
        <p className="mt-3 text-sm text-apagado">
          {vista.porColumna
            ? tr('Ningún caso con esos filtros.', 'No cases with those filters.')
            : tr('Sin casos. Todo en orden.', 'No cases. All in order.')}
        </p>
      ) : (
        <div className="mt-3 max-h-[32rem] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                {marcable && (
                  <th className="w-8 py-1">
                    <input
                      type="checkbox"
                      checked={paginaMarcada}
                      onChange={e => marcar(deLaPagina, e.target.checked)}
                      aria-label={tr('Marcar los casos de esta página', 'Tick the cases on this page')}
                    />
                  </th>
                )}
                {detalle.columnas.map(c => (
                  <th
                    key={c.id}
                    className={`py-1 pr-3 font-semibold ${c.numerica ? 'text-right' : ''}`}
                    aria-sort={
                      vista.orden === c.id ? (vista.direccion === 'desc' ? 'descending' : 'ascending') : undefined
                    }
                  >
                    <button
                      type="button"
                      onClick={() => ordenarPor(c.id)}
                      className="font-semibold uppercase tracking-wide hover:text-tinta"
                      title={tr('Ordenar por esta columna', 'Sort by this column')}
                    >
                      {c.titulo}
                      {vista.orden === c.id ? (vista.direccion === 'desc' ? ' ▾' : ' ▴') : ''}
                    </button>
                  </th>
                ))}
                {conCorregir && <th className="py-1 font-semibold" />}
              </tr>
            </thead>
            <tbody>
              {detalle.filas.map(f => (
                <tr key={f.clave} className="border-t border-linea/70">
                  {marcable && (
                    <td className="py-1">
                      <input
                        type="checkbox"
                        checked={seleccion.has(f.clave)}
                        onChange={e => marcar([f], e.target.checked)}
                        aria-label={tr(`Marcar ${nombreDelCaso(f)}`, `Tick ${nombreDelCaso(f)}`)}
                      />
                    </td>
                  )}
                  {detalle.columnas.map(c => {
                    const valor = f.valores[c.id];
                    const texto = formatear(valor, c.numerica);
                    return (
                      <td key={c.id} className={`py-1 pr-3 ${c.numerica ? 'text-right tabular-nums' : ''}`}>
                        {c.id === primera && f.enlace ? (
                          <Link to={f.enlace} className="font-medium text-verde hover:underline">
                            {texto}
                          </Link>
                        ) : (
                          texto
                        )}
                      </td>
                    );
                  })}
                  {conCorregir && (
                    <td className="py-1 text-right whitespace-nowrap">
                      {corregir(f.enlace) && (
                        <Link
                          to={corregir(f.enlace)!}
                          className="rounded-md border border-verde px-2 py-0.5 text-xs text-verde-oscuro hover:bg-verde-claro"
                        >
                          {tr('✎ Corregir', '✎ Fix')}
                        </Link>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detalle.totalPaginas > 1 && (
        <div className="mt-2 flex items-center justify-end gap-2 text-sm">
          <button
            type="button"
            onClick={() => onPagina(detalle.pagina - 1)}
            disabled={detalle.pagina <= 1}
            className="rounded-md border border-linea bg-white px-2.5 py-1 hover:bg-papel disabled:opacity-40"
          >
            ‹ {tr('Anterior', 'Previous')}
          </button>
          <span className="tabular-nums text-apagado">
            {numero(detalle.pagina)} / {numero(detalle.totalPaginas)}
          </span>
          <button
            type="button"
            onClick={() => onPagina(detalle.pagina + 1)}
            disabled={detalle.pagina >= detalle.totalPaginas}
            className="rounded-md border border-linea bg-white px-2.5 py-1 hover:bg-papel disabled:opacity-40"
          >
            {tr('Siguiente', 'Next')} ›
          </button>
        </div>
      )}
    </div>
  );
}

function formatear(valor: string | number | boolean | null | undefined, numerica?: boolean): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (typeof valor === 'boolean') return valor ? tr('Sí', 'Yes') : 'No';
  if (typeof valor === 'number') return numero(valor, Number.isInteger(valor) ? 0 : 2);
  if (/^\d{4}-\d{2}-\d{2}T/.test(valor)) return fechaHora(valor);
  if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) return fecha(valor);
  if (numerica) return numero(valor);
  return valor;
}

function Reglas({tablero}: {tablero: TableroDeCalidad}) {
  const r = tablero.reglas;
  return (
    <div className="rounded-lg border border-linea bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium">{tr('Reglas vigentes', 'Current rules')}</h2>
        <span className="text-xs text-apagado">
          {tr(
            'Tomadas del código real: lo que se muestra es lo que la app aplica.',
            'Taken from the actual code: what is shown is what the app applies.',
          )}
        </span>
      </div>
      <div className="mt-3 grid gap-4 xl:grid-cols-3">
        <div>
          <h3 className="text-xs font-semibold tracking-wide text-apagado uppercase">
            {tr('Prefijo del BB → mina, proveedor y exportador', 'BB prefix → mine, supplier and exporter')}
          </h3>
          <table className="mt-1 w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-apagado">
                <th className="py-1 pr-2 font-semibold">
                  {tr('Prefijo', 'Prefix')}
                  <NotaDeAyuda texto={A.prefijoBb} />
                </th>
                <th className="py-1 pr-2 font-semibold">
                  {tr('Mina', 'Mine')}
                  <NotaDeAyuda texto={A.minaRegla} />
                </th>
                <th className="py-1 pr-2 font-semibold">
                  {tr('Proveedor', 'Supplier')}
                  <NotaDeAyuda texto={A.proveedorRegla} />
                </th>
                <th className="py-1 pr-2 font-semibold">
                  {tr('Exportador', 'Exporter')}
                  <NotaDeAyuda texto={A.exportadorRegla} />
                </th>
                <th className="py-1 text-right font-semibold">
                  BB
                  <NotaDeAyuda texto={A.bbPrefijo} />
                </th>
              </tr>
            </thead>
            <tbody>
              {r.prefijos.map(p => (
                <tr key={p.prefijo} className={`border-t border-linea/70 ${p.conocido ? '' : 'text-apagado'}`}>
                  <td className="py-1 pr-2 font-medium">
                    {p.prefijo}
                    {!p.conocido && (
                      <span
                        title={tr(
                          'El prefijo no está en la regla: cae en los valores por defecto',
                          'The prefix is not in the rule: it falls back to the default values',
                        )}
                      >
                        {' '}
                        *
                      </span>
                    )}
                  </td>
                  <td className="py-1 pr-2">{p.mina}</td>
                  <td className="py-1 pr-2">{p.proveedor}</td>
                  <td className="py-1 pr-2">{p.exportador}</td>
                  <td className="py-1 text-right tabular-nums">{numero(p.bigBags)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1 text-xs text-apagado">
            {tr('* Sin regla propia: por defecto', '* No rule of its own: defaults to')} {r.porDefecto.mina} /{' '}
            {r.porDefecto.proveedor} / {r.porDefecto.exportador}.
          </p>
        </div>
        <div>
          <h3 className="text-xs font-semibold tracking-wide text-apagado uppercase">
            {tr('Proveedor → exportador', 'Supplier → exporter')}
          </h3>
          <table className="mt-1 w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-apagado">
                <th className="py-1 pr-2 font-semibold">
                  {tr('Proveedor', 'Supplier')}
                  <NotaDeAyuda texto={A.proveedor} />
                </th>
                <th className="py-1 pr-2 font-semibold">
                  {tr('Exportador', 'Exporter')}
                  <NotaDeAyuda texto={A.exportadorRegla} />
                </th>
                <th className="py-1 text-right font-semibold">
                  BB
                  <NotaDeAyuda texto={A.bbProveedor} />
                </th>
              </tr>
            </thead>
            <tbody>
              {r.proveedorAExportador.map(p => (
                <tr key={p.proveedor} className="border-t border-linea/70">
                  <td className="py-1 pr-2">{p.proveedor}</td>
                  <td className="py-1 pr-2">{p.exportador}</td>
                  <td className="py-1 text-right tabular-nums">{numero(p.bigBags)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-apagado">
            <span className="font-semibold">{tr('Planta:', 'Plant:')}</span> {r.planta}
          </p>
        </div>
        <div>
          <h3 className="text-xs font-semibold tracking-wide text-apagado uppercase">
            {tr('Prefijo del código de exportación → exportador', 'Export code prefix → exporter')}
          </h3>
          {r.exportadoresPorPrefijo.length === 0 ? (
            <p className="mt-1 text-sm text-apagado">{tr('Ninguno configurado.', 'None configured.')}</p>
          ) : (
            <table className="mt-1 w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-apagado">
                  <th className="py-1 pr-2 font-semibold">
                    {tr('Prefijo', 'Prefix')}
                    <NotaDeAyuda texto={A.prefijoExportacion} />
                  </th>
                  <th className="py-1 pr-2 font-semibold">
                    {tr('Exportador', 'Exporter')}
                    <NotaDeAyuda texto={A.exportadorRegla} />
                  </th>
                  <th className="py-1 font-semibold">
                    {tr('Activo', 'Active')}
                    <NotaDeAyuda texto={A.activo} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {r.exportadoresPorPrefijo.map(p => (
                  <tr key={p.prefijo} className="border-t border-linea/70">
                    <td className="py-1 pr-2 font-medium">{p.prefijo}</td>
                    <td className="py-1 pr-2">{p.exportador}</td>
                    <td className="py-1">{p.activo ? tr('Sí', 'Yes') : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Link to="/configuracion-de-campos/prefijos" className="mt-2 inline-block text-sm text-verde hover:underline">
            {tr('Administrar exportadores por prefijo →', 'Manage exporters by prefix →')}
          </Link>
        </div>
      </div>
    </div>
  );
}

function Cargas({
  tablero,
  cargaActiva,
  onAbrir,
}: {
  tablero: TableroDeCalidad;
  cargaActiva: number | null;
  onAbrir: (id: number) => void;
}) {
  return (
    <div className="rounded-lg border border-linea bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium">{tr('Cargas e incidencias', 'Uploads and issues')}</h2>
        <div className="flex gap-3 text-xs">
          <Link to="/inventario/cargas" className="text-verde hover:underline">
            {tr('Cargar control de empaque', 'Upload packing control')}
          </Link>
          <Link to="/seguimiento/cambios" className="text-verde hover:underline">
            {tr('Cargar cambios de exportaciones', 'Upload export changes')}
          </Link>
        </div>
      </div>
      {tablero.cargas.length === 0 ? (
        <p className="mt-2 text-sm text-apagado">{tr('Todavía no hay cargas.', 'There are no uploads yet.')}</p>
      ) : (
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-apagado">
              <th className="py-1 pr-3 font-semibold">
                {tr('Fecha', 'Date')}
                <NotaDeAyuda texto={C.fecha} />
              </th>
              <th className="py-1 pr-3 font-semibold">
                {tr('Tipo', 'Type')}
                <NotaDeAyuda texto={C.tipo} />
              </th>
              <th className="py-1 pr-3 font-semibold">
                {tr('Archivo', 'File')}
                <NotaDeAyuda texto={C.archivo} />
              </th>
              <th className="py-1 pr-3 font-semibold">
                {tr('Estado', 'Status')}
                <NotaDeAyuda texto={C.estado} />
              </th>
              <th className="py-1 pr-3 text-right font-semibold">
                {tr('Filas', 'Rows')}
                <NotaDeAyuda texto={C.filas} />
              </th>
              <th className="py-1 pr-3 text-right font-semibold">
                {tr('Incidencias', 'Issues')}
                <NotaDeAyuda texto={C.incidencias} />
              </th>
              <th className="py-1 pr-3 text-right font-semibold">
                {tr('Sin decisión', 'Undecided')}
                <NotaDeAyuda texto={C.sinDecision} />
              </th>
              <th className="py-1 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {tablero.cargas.map(c => (
              <tr key={c.id} className={`border-t border-linea/70 ${cargaActiva === c.id ? 'bg-verde-claro/50' : ''}`}>
                <td className="py-1 pr-3 whitespace-nowrap">{fechaHora(c.creadaEl)}</td>
                <td className="py-1 pr-3">{c.tipoEtiqueta}</td>
                <td className="max-w-64 truncate py-1 pr-3" title={c.archivo ?? ''}>
                  {textoOGuion(c.archivo)}
                </td>
                <td className="py-1 pr-3">
                  {c.estadoEtiqueta}
                  {c.error ? ` · ${c.error}` : ''}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">{numero(c.filas)}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{numero(c.incidencias)}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{numero(c.incidenciasSinDecision)}</td>
                <td className="py-1 text-right">
                  {c.incidencias > 0 && (
                    <button type="button" onClick={() => onAbrir(c.id)} className="text-verde hover:underline">
                      {cargaActiva === c.id ? tr('Ocultar', 'Hide') : tr('Ver incidencias', 'View issues')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EnlaceDeRegistro({entidad, clave}: {entidad: string | null; clave: string | null}) {
  if (!clave) return <>—</>;
  if (entidad === 'big_bag') {
    return (
      <Link to={`/inventario/${encodeURIComponent(clave)}`} className="text-verde hover:underline">
        {clave}
      </Link>
    );
  }
  if (entidad === 'export') {
    return (
      <Link to={`/seguimiento/${encodeURIComponent(clave)}`} className="text-verde hover:underline">
        {clave}
      </Link>
    );
  }
  return <>{clave}</>;
}
